import { Close as CloseIcon } from "@mui/icons-material";
import { IconButton, TextField, type TextFieldProps, type TextFieldVariants, Typography } from "@mui/material";
import type { BaseSyntheticEvent } from "react";
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

type FormTextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  Variant extends TextFieldVariants = TextFieldVariants,
> = Omit<TextFieldProps<Variant>, "name" | "onChange"> & {
  name: TName;
  control: Control<TFieldValues>;
  hideErrMessage?: boolean;
  onChange?: (value: string) => void;
  hideCloseIcon?: boolean;
};

export default function FormTextField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: FormTextFieldProps<TFieldValues, TName>) {
  const {
    autoFocus = false,
    control,
    disabled = false,
    fullWidth = true,
    helperText,
    hidden,
    hideErrMessage = false,
    label,
    name,
    margin = "normal",
    onBlur,
    onChange,
    placeholder = "",
    size,
    style = {},
    type = "",
    variant = "outlined",
    hideCloseIcon = false,
    ...restProps
  } = props;

  const handleClearText = (field: ControllerRenderProps<TFieldValues, TName>) => {
    if (onChange) onChange("");
    field.onChange("");
  };

  const handleChange = (event: BaseSyntheticEvent<InputEvent>, field: ControllerRenderProps<TFieldValues, TName>) => {
    if (onChange) onChange(event.target?.value);
    field.onChange(event.target?.value);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { error, invalid, isDirty, isTouched } = fieldState;
        return (
          <TextField
            name={field.name}
            ref={field.ref}
            disabled={disabled}
            fullWidth={fullWidth}
            autoFocus={autoFocus}
            type={type}
            label={label}
            margin={margin}
            size={size}
            placeholder={placeholder}
            variant={variant}
            style={{
              display: hidden && !field.value ? `none` : undefined,
              ...style,
            }}
            sx={{
              ".MuiFormLabel-root": {
                textTransform: "capitalize",
              },
            }}
            error={!hideErrMessage && (isDirty || isTouched) && invalid}
            FormHelperTextProps={{ component: "div" }}
            helperText={
              <Typography variant="error">
                {(!hideErrMessage && (isDirty || isTouched) && invalid && error?.message) || helperText}
              </Typography>
            }
            value={field.value}
            onInput={(e: BaseSyntheticEvent<InputEvent>) => handleChange(e, field)} // Handle input event
            onBlur={(e) => {
              if (onBlur) onBlur(e);
              field.onBlur();
            }}
            InputProps={{
              endAdornment: hideCloseIcon ? null : (
                <IconButton
                  onClick={() => handleClearText(field)}
                  sx={{ visibility: field.value ? "visible" : "hidden" }}
                >
                  <CloseIcon sx={{ color: (theme) => theme.palette.neutral[600] }} />
                </IconButton>
              ),
            }}
            {...restProps}
          />
        );
      }}
    />
  );
}
